import React, {useCallback, useEffect, useMemo, useState} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, SlidersHorizontal } from "lucide-react";
import styles from "./ChatPage.module.scss";
import { MOBILE_QUERY, useMediaQuery } from "../utilities/useMediaQuery";
import {Header} from "../components/Header";
import {Chat} from "../components/Chat";
import {SideBarLeft} from "../components/SideBarLeft";
import { FlightSearchSummary, type ChatHistoryItem, type ChatHistorySummary, type SidebarView } from "../components/FlightSearchSummary";
import { inferFilterPhases, loadChats, newChatId, saveChats, storableMessages, titleFromMessages, upsertChat, type StoredChat } from "../services/chatStore";
// import SideBarRightTest from "../unused_components/SideBarRightTest";
import {SideBarRight} from "../components/SideBarRight";
import type { FilterPhase, FlightFilters, HotelFilters, DestinationFilters, RoomFilters, SelectedFlight, SelectedHotel, StaticDates } from "../types/filterSidebar";

const DEFAULT_FLIGHT_FILTERS: FlightFilters = {
    stops: 'any', sort: 'best', maxPrice: null, time: 'any', airlines: [],
};
const DEFAULT_HOTEL_FILTERS: HotelFilters = {
    stars: [], maxPrice: null, amenities: [], propertyType: [], guestRating: 0,
};
const DEFAULT_DESTINATION_FILTERS: DestinationFilters = {
    regions: [], categories: [], moods: [], maxPrice: null,
};
const DEFAULT_ROOM_FILTERS: RoomFilters = {
    bedType: 'Any', maxPrice: null, amenities: [],
};

/** Reads the message handed over by `navigate('/chat', { state: { message } })`. */
const messageFromState = (state: unknown): string | undefined => {
    if (!state || typeof state !== 'object') return undefined;
    const raw = (state as { message?: unknown }).message;
    if (typeof raw !== 'string') return undefined;
    const trimmed = raw.trim();
    return trimmed ? trimmed : undefined;
};

export const ChatPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [pendingMessage, setPendingMessage] = useState<string | undefined>(undefined);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [filterCollapsed, setFilterCollapsed] = useState(false);
    // Below MOBILE_QUERY the two rails leave the chat column no width at
    // all, so they come out of the flow and open over it one at a time.
    const isMobile = useMediaQuery(MOBILE_QUERY);
    const [mobileDrawer, setMobileDrawer] = useState<'none' | 'nav' | 'filters'>('none');
    const [flightFilters, setFlightFilters] = useState<FlightFilters>(DEFAULT_FLIGHT_FILTERS);
    const [hotelFilters, setHotelFilters] = useState<HotelFilters>(DEFAULT_HOTEL_FILTERS);
    const [destinationFilters, setDestinationFilters] = useState<DestinationFilters>(DEFAULT_DESTINATION_FILTERS);
    const [roomFilters, setRoomFilters] = useState<RoomFilters>(DEFAULT_ROOM_FILTERS);
    const [selectedFlight, setSelectedFlight] = useState<SelectedFlight | null>(null);
    const [selectedHotel, setSelectedHotel] = useState<SelectedHotel | null>(null);
    const [activeView, setActiveView] = useState<SidebarView>('searches');
    const [summary, setSummary] = useState<ChatHistorySummary | undefined>(undefined);
    const [slotState, setSlotState] = useState<Record<string, unknown>>({});
    const [scrollToMessageId, setScrollToMessageId] = useState<string | undefined>(undefined);
    const [rightPanelPhase, setRightPanelPhase] = useState<FilterPhase>('idle');
    const [staticDates, setStaticDates] = useState<StaticDates>({ checkIn: '', checkOut: '' });

    // Chat history — "New trip" starts a fresh id, the active chat is
    // written back to localStorage as it grows, and picking a RECENT item
    // just switches which id is active.
    const [chats, setChats] = useState<StoredChat[]>(() => loadChats());
    const [activeChatId, setActiveChatId] = useState<string>(() => newChatId());

    useEffect(() => {
        saveChats(chats);
    }, [chats]);

    // Rotating to landscape with a drawer open would otherwise leave the
    // scrim painted over a layout that no longer has drawers.
    useEffect(() => {
        if (!isMobile) setMobileDrawer('none');
    }, [isMobile]);

    useEffect(() => {
        if (mobileDrawer === 'none') return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMobileDrawer('none');
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [mobileDrawer]);

    /**
     * A trip handed over from the home page, a destination card or the
     * profile page arrives as router state. Consume it once and strip it
     * from the history entry, so a reload or a Back/Forward hop doesn't
     * replay the same message into a fresh thread.
     */
    useEffect(() => {
        const handed = messageFromState(location.state);
        if (!handed) return;
        setPendingMessage(handed);
        navigate(location.pathname, { replace: true, state: null });
    }, [location.key, location.pathname, location.state, navigate]);

    // Sorted by createdAt, not updatedAt — opening or adding to a chat
    // must not move it in the list, only creating a new one does.
    const sortedChats = useMemo(
        () => [...chats].sort((a, b) => b.createdAt - a.createdAt),
        [chats]
    );

    const handleNewChat = () => {
        setActiveChatId(newChatId());
        // Otherwise the new chat's SideBarRight would remount seeded with
        // whatever phase was last live on the chat being left.
        setRightPanelPhase('idle');
        setMobileDrawer('none');
    };

    const handleChatSelect = (id: string) => {
        setActiveChatId(id);
        setRightPanelPhase('idle');
        setMobileDrawer('none');
    };

    const handleChatDelete = (id: string) => {
        const title = chats.find((chat) => chat.id === id)?.title || 'this chat';
        if (!window.confirm(`Delete "${title}"? This can't be undone.`)) return;
        setChats((prev) => prev.filter((chat) => chat.id !== id));
        // Deleting the chat that's open drops the user into a fresh one
        // rather than leaving a thread on screen that no longer exists.
        if (id === activeChatId) {
            setActiveChatId(newChatId());
            setRightPanelPhase('idle');
        }
    };

    // What SideBarRight should show for the chat now becoming active —
    // computed from its own history, not left over from whichever chat
    // was open before.
    const activeChatFilterSeed = useMemo(
        () => inferFilterPhases(chats.find((chat) => chat.id === activeChatId)?.messages ?? []),
        [chats, activeChatId]
    );

    const handleMessagesChange = useCallback((messages: import("../components/Chat").ChatMessage[]) => {
        const stored = storableMessages(messages);
        if (stored.length === 0) return;
        setChats((prev) => upsertChat(prev, {
            id: activeChatId,
            title: titleFromMessages(stored),
            createdAt: Date.now(),
            updatedAt: Date.now(),
            messages: stored,
        }));
    }, [activeChatId]);

    const handleClearAll = () => {
        setFlightFilters(DEFAULT_FLIGHT_FILTERS);
        setHotelFilters(DEFAULT_HOTEL_FILTERS);
        setDestinationFilters(DEFAULT_DESTINATION_FILTERS);
        setRoomFilters(DEFAULT_ROOM_FILTERS);
    };


    // The desktop collapse states and the mobile drawer states are
    // mutually exclusive: on mobile the rails are fixed overlays, and a
    // 68px-wide collapsed drawer would make no sense.
    const leftSectionClass = [
        styles.leftSection,
        !isMobile && sidebarCollapsed ? styles.leftSectionCollapsed : '',
        isMobile && mobileDrawer === 'nav' ? styles.leftSectionOpen : '',
    ].filter(Boolean).join(' ');

    const rightSectionClass = [
        styles.rightSection,
        !isMobile && filterCollapsed ? styles.rightSectionCollapsed : '',
        isMobile && mobileDrawer === 'filters' ? styles.rightSectionOpen : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={styles.ChatPage}>
            <section className={leftSectionClass}>
                {/*<Header compact />*/}
                {/*<FlightSearchSummary*/}
                {/*    activeView={activeView}*/}
                {/*    onViewChange={setActiveView}*/}
                {/*    summary={summary}*/}
                {/*    onSelectHistoryItem={(item: ChatHistoryItem) => {*/}
                {/*        setActiveView(item.widgetType === 'order_confirmation' ? 'orders' : 'searches');*/}
                {/*        setScrollToMessageId(item.id);*/}
                {/*    }}*/}
                {/*/>*/}

                {/*<div className={styles.divSearch}>*/}
                {/*    <input id='searchInput' type="text" placeholder="Search..." className={styles.inputSearch}/>*/}
                {/*    <div className={styles.divLanguage}>*/}
                {/*        <SVGGlobe width="20" height="20"/>*/}
                {/*        <p className={styles.pLanguage}>En</p>*/}
                {/*    </div>*/}
                {/*</div>*/}
                <SideBarLeft
                    collapsed={!isMobile && sidebarCollapsed}
                    onToggle={() => setSidebarCollapsed(prev => !prev)}
                    onNewChat={handleNewChat}
                    onMobileClose={() => setMobileDrawer('none')}
                    isMobile={isMobile}
                    chats={sortedChats.map((chat) => ({ id: chat.id, title: chat.title, updatedAt: chat.updatedAt }))}
                    activeChatId={activeChatId}
                    onChatSelect={handleChatSelect}
                    onChatDelete={handleChatDelete}
                />

                {/*<div className={styles.divSavedOrders}>*/}
                {/*    <span className={styles.spanOptions}>Orders</span>*/}
                {/*</div>*/}
                {/*<div className={styles.divSavedSearches}>*/}
                {/*    <span className={styles.spanOptions}>Search Results</span>*/}
                {/*</div>*/}

                {/*<Footer />*/}

                {/*this is Footer substitute for ChatPage Left Section.*/}
                {/*<div className={styles.divCopyRight}>*/}
                {/*    <p>© 2024 Adiona Travel. All rights reserved. Transforming travel planning with AI.</p>*/}
                {/*</div>*/}
            </section>
            <section className={styles.middleSection}>
                {/* Gives the column a top edge to match the sidebar's brand
                    row and the filter panel's header, and names the thread. */}
                <div className={styles.threadBar}>
                    {isMobile && (
                        <button
                            className={styles.threadBarBtn}
                            onClick={() => setMobileDrawer('nav')}
                            aria-label="Open trips"
                            title="Trips"
                        >
                            <Menu size={18} aria-hidden="true" />
                        </button>
                    )}
                    <span className={styles.threadTitle}>
                        {chats.find((chat) => chat.id === activeChatId)?.title || 'New trip'}
                    </span>
                    {isMobile && (
                        <button
                            className={styles.threadBarBtn}
                            onClick={() => setMobileDrawer('filters')}
                            aria-label="Open filters"
                            title="Filters"
                        >
                            <SlidersHorizontal size={18} aria-hidden="true" />
                        </button>
                    )}
                </div>
                <div className={styles.chatBody}>
                    {/*<ChatThread*/}
                    {/*    activeView={activeView}*/}
                    {/*    onViewChange={setActiveView}*/}
                    {/*    onSummaryChange={setSummary}*/}
                    {/*    onSlotStateChange={setSlotState}*/}
                    {/*    scrollToMessageId={scrollToMessageId}*/}
                    {/*    onScrollHandled={() => setScrollToMessageId(undefined)}*/}
                    {/*/>*/}
                    <Chat
                        key={activeChatId}
                        initialMessages={chats.find((chat) => chat.id === activeChatId)?.messages}
                        onMessagesChange={handleMessagesChange}
                        pendingMessage={pendingMessage}
                        onPendingMessageHandled={() => setPendingMessage(undefined)}
                        activeView={activeView}
                        onSummaryChange={setSummary}
                        onSlotStateChange={setSlotState}
                        onContentTypeChange={setRightPanelPhase}
                        onStaticDatesChange={setStaticDates}
                        staticCheckIn={staticDates.checkIn}
                        staticCheckOut={staticDates.checkOut}
                        scrollToMessageId={scrollToMessageId}
                        onScrollHandled={() => setScrollToMessageId(undefined)}
                    />
                </div>
            </section>
            <section className={rightSectionClass}>
                {/*<SideBarRightTest slotState={slotState} />*/}

                <SideBarRight
                    key={activeChatId}
                    collapsed={!isMobile && filterCollapsed}
                    // As a bottom sheet there is nothing to collapse to, so
                    // the panel's own chevron dismisses it instead.
                    onToggle={isMobile
                        ? () => setMobileDrawer('none')
                        : () => setFilterCollapsed(prev => !prev)}
                    phase={rightPanelPhase}
                    initialSeenPhases={activeChatFilterSeed.seen}
                    initialOpenPhase={activeChatFilterSeed.open}
                    flightFilters={flightFilters}
                    onFlightFiltersChange={setFlightFilters}
                    hotelFilters={hotelFilters}
                    onHotelFiltersChange={setHotelFilters}
                    destinationFilters={destinationFilters}
                    onDestinationFiltersChange={setDestinationFilters}
                    roomFilters={roomFilters}
                    onRoomFiltersChange={setRoomFilters}
                    selectedFlight={selectedFlight}
                    selectedHotel={selectedHotel}
                    onRemoveFlight={() => setSelectedFlight(null)}
                    onRemoveHotel={() => setSelectedHotel(null)}
                    onClearAll={handleClearAll}
                    staticCheckIn={staticDates.checkIn}
                    staticCheckOut={staticDates.checkOut}
                    onStaticDatesChange={setStaticDates}
                />
                {/*<Dropdown title="Hotels">*/}
                {/*    <HotelInfo />*/}
                {/*</Dropdown>*/}
                {/*<Dropdown title="Airfare">*/}
                {/*    <AirfareInfo />*/}
                {/*</Dropdown>*/}
            </section>
            {isMobile && mobileDrawer !== 'none' && (
                <div
                    className={styles.mobileScrim}
                    onClick={() => setMobileDrawer('none')}
                    aria-hidden="true"
                />
            )}
        </div>
    );
};
